<template>
  <v-container fluid fill-height class="login">
    <v-row>
      <v-spacer></v-spacer>
      <v-col>
        <v-card class="elevation-12 loginCard pa-3">
          <v-card-title>
            <v-img
                src="@/assets/logo-texte.png"
                contain
                height="200"
                width="200"
            />
          </v-card-title>
          <v-card-text>
            <v-alert type="info" v-if="$route.query.expired">Session expirée. Veuillez vous réauthentifier.</v-alert>
            <v-form :disabled="loading || loadingSso" lazy-validation ref="form">
              <v-text-field
                  prepend-icon="mdi-account-box"
                  v-model="form.username"
                  label="Identifiant"
                  type="text"
                  :rules="[isRequiredGenerator('Identifiant obligatoire')]"
                  @keyup.enter="login()"
              ></v-text-field>
              <v-text-field
                  id="password"
                  v-model="form.password"
                  prepend-icon="mdi-lock"
                  label="Mot de passe"
                  type="password"
                  :rules="[isRequiredGenerator('Mot de passe obligatoire')]"
                  @keyup.enter="login()"
              ></v-text-field>
            </v-form>

            <error-alert :showError="!!errorMessage" :errorMessage="errorMessage"/>

          </v-card-text>
          <div class="d-flex flex-column">
            <v-btn color="primary" :loading="loading" :disabled="loadingSso" block @click="login()">Authentification</v-btn>
            <sso-button @login-sso='getTokenBySso' :loading="loadingSso" :disabled="loading" @click="onSsoLoginClick"
                        @error="showMessageError('Une erreur est survenue lors de l\'authentification SSO. Veuillez réessayer ou contacter l\'administrateur')"/>
          </div>
        </v-card>
      </v-col>
      <v-spacer></v-spacer>
    </v-row>
  </v-container>
</template>

<script lang="ts">
import Vue from 'vue'
import { AuthenticationResult } from '@azure/msal-browser'
import ErrorAlert from '@/components/alert/ErrorAlert.vue'
import { loginApi } from '@/api/login'
import store from '@/store'
import SsoButton from '@/components/SsoButton.vue'
import { isRequiredGenerator } from '@/utils/rules'
import msalInstance from '@/msal-config'
import { isSafari } from '@/utils/brower'
import { handleAxiosException } from '@/api'

const MESSAGE_ERROR = "Une erreur est survenue lors de l'authentification. Veuillez réessayer ou contacter l'administrateur"
const MESSAGE_ERROR_SSO = "Une erreur est survenue lors de l'authentification SSO. Veuillez réessayer ou contacter l'administrateur"
const MESSAGE_ERROR_USER_DISABLE = "Votre compte est desactivé, veuillez contacter l'administrateur"

const REDIRECT_SSO_LOADING = 'redirect_loading'

export default Vue.extend({
  components: {
    SsoButton,
    ErrorAlert,
  },
  data() {
    return {
      form: {
        password: '',
        username: '',
      },
      errorMessage: '',
      authState: store.state,
      loading: false,
      loadingSso: false,
    }
  },
  methods: {
    isRequiredGenerator,
    routerRedirect() {
      const searchParams = new URLSearchParams(window.location.search)
      const url = searchParams.get('redirect')
      if (url) {
        this.$router.push(url)
      } else {
        this.$router.push('/')
      }
    },
    async login() {
      let msg = ''
      this.errorMessage = ''
      if ((this.$refs.form as HTMLFormElement).validate()) {
        this.loading = true
        try {
          const response = await loginApi.login(this.form.username, this.form.password)
          store.setUserInfo(response)
        } catch (error) {
          msg = handleAxiosException(error, r => r.data?.message ?? MESSAGE_ERROR)
        }
        if (!this.authState.authenticated) {
          this.loading = false
          this.form.password = ''
          this.errorMessage = msg === MESSAGE_ERROR_USER_DISABLE ? msg : 'Mauvais identifiant ou mot de passe'
        } else {
          this.errorMessage = msg
          this.routerRedirect()
        }
      }
    },
    onSsoLoginClick() {
      this.loadingSso = true
      if (isSafari()) {
        sessionStorage.setItem(REDIRECT_SSO_LOADING, 'true')
      }
      this.errorMessage = ''
    },
    async getTokenBySso(responseSso: AuthenticationResult) {
      let msg = ''
      this.errorMessage = ''
      this.loadingSso = true
      if (responseSso.account?.name != null) {
        try {
          const res = await loginApi.loginSso(responseSso.account.name, responseSso.accessToken)
          store.setUserInfo(res)
        } catch (e) {
          msg = handleAxiosException(e)
        }
        if (!this.authState.authenticated) {
          this.loadingSso = false
          this.errorMessage = msg === MESSAGE_ERROR_USER_DISABLE ? msg : MESSAGE_ERROR_SSO
        } else {
          this.errorMessage = msg
          this.routerRedirect()
        }
      } else {
        this.errorMessage = MESSAGE_ERROR_SSO
      }
    },
    showMessageError(message: string) {
      this.errorMessage = message
      this.loading = false
      this.loadingSso = false
    },
  },

  mounted() {
    this.loading = false
    this.loadingSso = sessionStorage.getItem(REDIRECT_SSO_LOADING) !== null
    if (isSafari()) {
      msalInstance.handleRedirectPromise().then((tokenResponse: AuthenticationResult|null) => {
        if (tokenResponse != null && this.loadingSso) {
          this.getTokenBySso(tokenResponse)
        } else {
          this.loadingSso = false
        }
        sessionStorage.removeItem(REDIRECT_SSO_LOADING)
      })
    }
  },
})
</script>

<style lang="scss" scoped>

.login {
  background: url('../assets/background_login.jpg');
  background-size: cover;
  height: 100vh;
}

.loginCard {
  background-color: #FFFFFFD0;
  opacity : 0.9;
}

.loginCard .v-btn {
  border-radius: 5px;
  font-weight: 400;
}
</style>
